import {config} from "../../../utils2";
import {fetchWrapper} from "../../../_services/fetch";
import {useAuth} from "../../../context";
import {useEffect, useState} from "react";
import {useParams, useSearchParams} from "react-router-dom";
import * as yup from "yup";

const api = config.get_remote_api() + "/api/order"

const endpointOrderOptions = (userId) => `${api}/order`
const endpointDetails = (targetId) => `${api}/${targetId}`
const endpointCount = (userId, search) => `${api}/count/${userId}${fetchWrapper.getSearchParams(search)}`
const endpointList = (userId, search) => `${api}/list/${userId}${fetchWrapper.getSearchParams(search)}`
const endpointCountAllOrders = (search) => `${api}/count${fetchWrapper.getSearchParams(search)}`
const endpointListAllOrders = (search) => `${api}/list${fetchWrapper.getSearchParams(search)}`

const endpointAdd = (userId) => `${api}/${userId}`
const endpointEdit = (targetId) => `${api}/${targetId}`
const endpointSmallEdit = (targetId) => `${api}/small-edit/${targetId}`
const endpointDelete = (targetId) => `${api}/${targetId}`

const endpointInvoice = (targetId) => `${api}/invoice/${targetId}`

const msg = "Failed with no error messages from the server."
const handleSuccess = resp => resp.success ? Promise.resolve(resp?.data) : Promise.reject({message: resp.message || msg})
const handleError = err => err?.name !== "AbortError" ? err?.error || err?.message || msg : null

const defaultValues = {
    id: null,
    status: "",
    order_number: "",

    shipping_name: "",
    shipping_line1: "",
    shipping_line2: "",
    shipping_city: "",
    shipping_zip: "",
    shipping_country: "",
    shipping_region: "",
    shipping_phone: "",

    billing_name: "",
    billing_line1: "",
    billing_line2: "",
    billing_city: "",
    billing_zip: "",
    billing_country: "",
    billing_region: "",
    billing_phone: "",

    shipping_track: "",
    shipping_tracking_url: "",
    shipping_company: "",
    shipping_service: "",
    shipping_price_in_cents: "",

    send_email: false,

    note: "",
    voucher: "",
    payment_method: "",
    payment_details: "",

    total_price_in_cents: 0,


    created_at: null,
    updated_at: null,
    shipped_at: null,

    order_articles: []
}

export function useOrder(oid = null) {
    const validationSchema = yup.object().shape({})

    const {token} = useAuth()
    const {value, setValue, loadingValue, setLoadingValue, errorValue, setErrorValue} = useOrderDetails(oid, defaultValues)

    function handleSuccessInt(resp) {
        if (resp.success) {
            setValue(resp?.data ? resp.data : defaultValues);
            return Promise.resolve(resp.message);
        } else {
            return Promise.reject({message: resp?.message || "Fetch failed with no error messages from the server."});
        }
    }

    const handleErrorInt = err => {
        setErrorValue(handleError(err))
        return Promise.reject(handleError(err))
    }


    function addItem(formValue, userId) {
        const {id, ...valuesNoId} = formValue
        return fetchWrapper.post(endpointAdd(userId), valuesNoId, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function editItem(formValue, targetId = oid) {
        return fetchWrapper.put(endpointEdit(targetId), formValue, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function smallEditItem(formValue, targetId = oid) {
        return fetchWrapper.put(endpointSmallEdit(targetId), formValue, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function deleteItem(targetId = oid) {
        return fetchWrapper.delete(endpointDelete(targetId), token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function getInvoice(targetId = oid) {
        return fetchWrapper.getFile(endpointInvoice(targetId), token.jwt)
            .then(response => {
                const contentType = response.headers.get('Content-Type');

                if (response.ok && contentType && contentType.startsWith('application/pdf')) {
                    return response.blob().then(blob => {
                        // Create a link to trigger the download
                        const link = document.createElement('a');
                        link.href = window.URL.createObjectURL(blob);
                        link.download = response.headers.get('Filename') || "invoice.pdf"
                        link.click();
                    });
                } else if (response.status === 404) {
                    throw new Error('Invalid Orders Number.');
                } else if (response.status === 406) {
                    throw new Error('Orders not shipped yet.');
                } else {
                    throw new Error('Cannot fetch invoice. Try later.');
                }
            })
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    return {
        value,
        validationSchema,
        addItem,
        editItem,
        smallEditItem,
        deleteItem,
        getInvoice,
        loadingValue,
        errorValue,
        clearErrorValue: () => setErrorValue(null)
    }
}

export function useOrderDetails(orderId, defaultValue = defaultValues) {

    const {loggedIn, token} = useAuth()
    const [loadingValue, setLoadingValue] = useState(false);
    const [errorValue, setErrorValue] = useState(null);
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && orderId && orderId !== "add") {
            setErrorValue(null)
            setLoadingValue(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointDetails(orderId), requestOptions)
                .then(handleSuccess)
                .then(setValue)
                .catch(err => setErrorValue(handleError(err)))
                .finally(() => setLoadingValue(false))
        }

        return () => {
            controller.abort()
            setLoadingValue(false)
        }
    }, [loggedIn, orderId]);


    return {
        value,
        setValue,
        loadingValue,
        setLoadingValue,
        errorValue,
        setErrorValue,
        clearError: () => setErrorValue(null)
    }
}

export function useOrderCount(queryOption = null, allOrders = false) {

    const {loggedIn, userId, token} = useAuth()
    const {uid} = useParams()
    const [searchParams] = useSearchParams()
    const [loadingCount, setLoadingCount] = useState(false);
    const [errorCount, setErrorCount] = useState(null);
    const [count, setCount] = useState(0);

    // Search options
    const query = queryOption || searchParams.get("query")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorCount(null)
            setLoadingCount(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {query}

            const endPoint = allOrders ? endpointCountAllOrders(search) : endpointCount(uid || userId, search)
            fetchWrapper.generic(endPoint, requestOptions)
                .then(handleSuccess)
                .then(setCount)
                .catch(err => setErrorCount(handleError(err)))
                .finally(() => setLoadingCount(false))
        }

        return () => {
            controller.abort()
            setLoadingCount(false)
        }
    }, [loggedIn, query]);

    return {
        count,
        setCount,
        loadingCount,
        errorCount,
        clearErrorCount: () => setErrorCount(null)
    }
}

export function useOrderList(pageSizeOption = null, pageIndexOption = null, queryOption = null, orderByOption = null, keepPrev = false, allOrders = false) {

    const defaultPageSize = 25
    const defaultPageIndex = 0

    const {loggedIn, userId, token} = useAuth()
    const {uid} = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const [loadingList, setLoadingList] = useState(false);
    const [errorList, setErrorList] = useState(null);
    const [list, setList] = useState([]);
    const [reload, setReload] = useState(0);

    // Search options
    const pageSize = pageSizeOption || Number(searchParams.get("size")) || defaultPageSize
    const pageIndex = pageIndexOption || Number(searchParams.get("index")) || defaultPageIndex
    const query = queryOption || searchParams.get("query")
    const orderBy = orderByOption || searchParams.get("by")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && pageIndex != null && pageSize != null) {
            setErrorList(null)
            setLoadingList(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {
                offset: pageIndex * pageSize,
                limit: pageSize,
                query: query,
                order_by: orderBy
            }

            const endPoint = allOrders ? endpointListAllOrders(search) : endpointList(uid || userId, search)
            fetchWrapper.generic(endPoint, requestOptions)
                .then(handleSuccess)
                .then(data => setList(prev => keepPrev ? ([...prev, ...data]) : data))
                .catch(err => setErrorList(handleError(err)))
                .finally(() => setLoadingList(false))

        }

        return () => {
            controller.abort()
            setLoadingList(false)
        }
    }, [loggedIn, pageIndex, pageSize, query, orderBy, reload]);

    return {
        list,
        setList: fields => {
            setList(prev => {
                const fieldsNew = (typeof fields === "function") ? fields(prev) : fields
                if (fieldsNew.length === 0 && pageIndex > 0) {
                    setSearchParams(prev => {
                        prev.set("index", `${pageIndex - 1}`)
                    })
                } else if (fieldsNew.length < prev.length) {
                    setReload(reload + 1)
                }
                return [...fieldsNew]
            })
        },
        loadingList,
        errorList,
        clearErrorList: () => setErrorList(null)
    }
}

export function useOrderOrderOptions(defaultOptions = []) {

    const {loggedIn, token} = useAuth()
    const {uid} = useParams()
    const [loadingOrderOptions, setLoadingOrderOptions] = useState(false);
    const [errorOrderOptions, setErrorOrderOptions] = useState(null);
    const [orderOptions, setOrderOptions] = useState(defaultOptions);

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorOrderOptions(null)
            setLoadingOrderOptions(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointOrderOptions(uid), requestOptions)
                .then(handleSuccess)
                .then(setOrderOptions)
                .catch(err => setErrorOrderOptions(handleError(err)))
                .finally(() => setLoadingOrderOptions(false))
        }

        return () => {
            controller.abort()
            setLoadingOrderOptions(false)
        }
    }, [loggedIn]);


    return {
        orderOptions,
        setOrderOptions,
        loadingOrderOptions,
        setLoadingOrderOptions,
        errorOrderOptions,
        setErrorOrderOptions,
        clearErrorOrderOptions: () => setErrorOrderOptions(null)
    }
}